import { Typography } from 'components/DesignSystemV2/Typography';
import { IconArrowArrowRightLine } from 'components/Icon/SVGIcons';
import FacebookIcon from 'components/Icon/SVGIcons/FacebookIcon';
import QuoteIcon from 'components/Icon/SVGIcons/QuoteIcon';
import { LazyImage } from 'components/LazyImage';
import LinkButton from 'components/LinkButton';
import React from 'react';
import { colorsV2 } from 'style/colors-v2';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const formatDate = date => {
  if (!date) return '';

  const dateObj = new Date(date);
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `Tháng ${month}, ${year}`;
};

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const UserWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: calc(100% - 64px);
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  display: grid;
  gap: 2px;
`;

const FeedBackImageWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 64px;
  aspect-ratio: 64/46;
  z-index: 1;

  img {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: 1px solid ${colorsV2.neutral20};
    border-radius: 16px;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 2;
  background-color: ${colorsV2.neutral5};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ReviewText = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ViewDetailButtonWrapper = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 70%;
  z-index: 2;
  height: 48px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  background: radial-gradient(
    100% 211.87% at 100% 100%,
    #f9fafc 47.71%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const QuoteIconWrapper = styled(QuoteIcon)`
  position: absolute;
  left: -1px;
  top: -3px;
  fill: ${colorsV2.neutral40};
  transition: fill 0.3s ease;

  path {
    stroke: ${colorsV2.neutral5};
    stroke-width: 5px;
    paint-order: stroke fill;
    transition: stroke 0.3s ease;
  }
`;

const ItemWrapper = styled(motion.li)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border: 1px solid ${colorsV2.neutral20};
  background-color: ${colorsV2.neutral5};
  border-radius: 24px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${colorsV2.neutral80};

    ${ViewDetailButtonWrapper} {
      opacity: 1;
      visibility: visible;
    }

    ${QuoteIconWrapper} {
      fill: ${colorsV2.neutral160};
    }
  }
`;

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};

const LinearThinkReviewsListItem = ({ node, index }) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const {
    avatarLink,
    coverLink,
    fullName,
    reviewContent,
    reviewDate,
    reviewLink
  } = node;

  return (
    <ItemWrapper
      ref={ref}
      variants={itemVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      transition={{
        duration: 0.5,
        delay: 0.1 * (index % 6),
        type: 'spring',
        stiffness: 100,
        damping: 15
      }}
    >
      <QuoteIconWrapper color="inherit" />
      <ReviewHeader>
        <UserWrapper>
          <Avatar>
            <LazyImage handle={avatarLink} alt={`${fullName}'s avatar`} />
          </Avatar>
          <UserInfo>
            <Typography variant="semi-bold/16-24">{fullName}</Typography>
            <Typography variant="regular/14-20" color={colorsV2.gray120}>
              {formatDate(reviewDate)}
            </Typography>
          </UserInfo>
        </UserWrapper>
        <FeedBackImageWrapper>
          <IconWrapper>
            <FacebookIcon />
          </IconWrapper>
          <LazyImage handle={coverLink} alt={`${fullName}'s feedback`} />
        </FeedBackImageWrapper>
      </ReviewHeader>
      <ReviewText>
        <Typography variant="regular/16-24" color={colorsV2.neutral180}>
          {reviewContent}
        </Typography>
      </ReviewText>
      <ViewDetailButtonWrapper>
        <LinkButton
          isExternal
          href={reviewLink}
          target="_blank"
          rel="noopener noreferrer"
          icon={<IconArrowArrowRightLine />}
        >
          Xem chi tiết
        </LinkButton>
      </ViewDetailButtonWrapper>
    </ItemWrapper>
  );
};

export default LinearThinkReviewsListItem;
